import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

export default function EmailTemplates() {
  const { API } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    API.get('/admin/email-templates').then(r => {
      setTemplates(r.data);
      if (r.data.length > 0) {
        setSelected(r.data[0]);
        setForm(r.data[0]);
      }
    });
  }, []);

  const selectTemplate = (t) => {
    setSelected(t);
    setForm({ ...t });
    setMsg('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put(`/admin/email-templates/${selected.id}`, {
        name: form.name,
        subject: form.subject,
        bodyHtml: form.body_html,
      });
      setMsg('Template saved successfully');
      // Refresh
      const r = await API.get('/admin/email-templates');
      setTemplates(r.data);
      const updated = r.data.find(t => t.id === selected.id);
      setSelected(updated);
    } catch (e) {
      setMsg('Error saving: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const TEMPLATE_LABELS = {
    tour_completed: 'Tour Completed → Admin Alert',
    admin_reminder: 'Admin Reminder (every 8hrs)',
    escalation_alert: 'Escalation → Regional Manager',
  };

  const TEMPLATE_VARS = {
    tour_completed: ['{{facility_name}}', '{{staff_name}}', '{{tour_date}}', '{{compliance_score}}', '{{issue_count}}', '{{issues_table}}', '{{tour_id}}', '{{app_url}}'],
    admin_reminder: ['{{facility_name}}', '{{tour_date}}', '{{hours_elapsed}}', '{{hours_remaining}}', '{{reminder_number}}', '{{issues_table}}', '{{tour_id}}', '{{app_url}}'],
    escalation_alert: ['{{facility_name}}', '{{admin_name}}', '{{admin_email}}', '{{tour_date}}', '{{hours_elapsed}}', '{{reminder_count}}', '{{issue_count}}', '{{issues_table}}', '{{tour_id}}', '{{app_url}}'],
  };

  return (
    <>
      <div className="page-header">
        <div><h2>Email Templates</h2><p>Customize automated alert emails sent by the system</p></div>
      </div>
      <div className="page-body">
        <div style={{display:'grid',gridTemplateColumns:'260px 1fr',gap:20}}>
          {/* Template List */}
          <div>
            {templates.map(t => (
              <div key={t.id}
                onClick={() => selectTemplate(t)}
                style={{
                  padding:'14px 16px', borderRadius:'var(--radius)', marginBottom:8,
                  border: selected?.id === t.id ? '2px solid var(--blue)' : '1px solid var(--gray-200)',
                  background: selected?.id === t.id ? '#eff6ff' : 'white',
                  cursor:'pointer', transition:'all 0.15s',
                }}
              >
                <div style={{fontSize:13,fontWeight:700,color:'var(--navy)',marginBottom:4}}>
                  {TEMPLATE_LABELS[t.template_key] || t.name}
                </div>
                <div style={{fontSize:11,color:'var(--gray-500)',textTransform:'uppercase',letterSpacing:'0.5px'}}>
                  {t.template_key === 'tour_completed' && '🔔 Triggered on submission'}
                  {t.template_key === 'admin_reminder' && '⏰ Every 8 hours'}
                  {t.template_key === 'escalation_alert' && '🚨 After 48 hours'}
                </div>
              </div>
            ))}
          </div>

          {/* Editor */}
          {selected && (
            <div className="card">
              <h3 className="card-title">{TEMPLATE_LABELS[selected.template_key] || selected.name}</h3>

              {msg && (
                <div className={`alert-banner ${msg.startsWith('Error') ? 'alert-danger' : 'alert-success'}`} style={{marginBottom:16}}>
                  {msg}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Template Name</label>
                <input className="form-control" value={form.name||''} onChange={e => setForm(p => ({...p, name: e.target.value}))}/>
              </div>

              <div className="form-group">
                <label className="form-label">Email Subject</label>
                <input className="form-control" value={form.subject||''} onChange={e => setForm(p => ({...p, subject: e.target.value}))}/>
                <div style={{fontSize:11,color:'var(--gray-500)',marginTop:4}}>
                  Supports variables: {(TEMPLATE_VARS[selected.template_key]||[]).filter(v => ['{{facility_name}}','{{compliance_score}}','{{hours_elapsed}}'].includes(v)).join(', ')}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Email Body (HTML)</label>
                <textarea
                  className="form-control"
                  style={{fontFamily:'var(--font-mono, monospace)',fontSize:12,minHeight:400}}
                  value={form.body_html||''}
                  onChange={e => setForm(p => ({...p, body_html: e.target.value}))}
                />
              </div>

              <div className="card" style={{background:'var(--gray-50)',marginBottom:20,padding:16}}>
                <div style={{fontSize:12,fontWeight:700,color:'var(--gray-700)',marginBottom:8}}>Available Variables</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                  {(TEMPLATE_VARS[selected.template_key] || []).map(v => (
                    <code key={v} style={{
                      background:'var(--navy)',color:'white',padding:'2px 8px',
                      borderRadius:'4px',fontSize:11,cursor:'pointer'
                    }} onClick={() => navigator.clipboard.writeText(v)} title="Click to copy">
                      {v}
                    </code>
                  ))}
                </div>
                <div style={{fontSize:11,color:'var(--gray-500)',marginTop:8}}>Click any variable to copy it to clipboard</div>
              </div>

              <div style={{display:'flex',justifyContent:'flex-end',gap:12}}>
                <button className="btn btn-secondary" onClick={() => setForm({ ...selected })}>Reset Changes</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : '💾 Save Template'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
